import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';

import { AuthLayout } from '@/components/layouts/auth-layout.jsx';
import { ProtectedAppShell } from '@/components/layouts/protected-app-shell.jsx';
import { ROUTES } from '@/config/routes.js';

// In production the root `/` is served by the Astro marketing build (outside
// this SPA). In local dev (`pnpm dev`) Vite serves the SPA for every path,
// so GitHub's OAuth redirect (which lands on `/?code=...`) would hit the
// `*` catch-all. This component bounces those requests into the SPA's own
// /login/callback handler; plain root visits fall through to the Not Found
// placeholder.
function RootRedirect() {
  const { search } = useLocation();
  const code = new URLSearchParams(search).get('code');
  if (code) return <Navigate to={`/login/callback${search}`} replace />;
  return <Placeholder label="Not Found" />;
}

function Placeholder({ label }) {
  return <div className="p-6 text-sm text-muted-foreground">{label} — coming soon</div>;
}

function lazyDefault(factory) {
  return async () => {
    const mod = await factory();
    return { Component: mod.default };
  };
}

// The marketing routes (/ and /pricing) are served by the Astro build
// at frontend/landing — not by this SPA. The SPA shell is only
// loaded by nginx for /admin/* and /login/callback paths, so the
// router below intentionally has no entry for the marketing surface.
// The Landing / Pricing JSX components themselves still live under
// src/features/{landing,pricing}/ because Astro imports them via @/
// for SSR.

export function createAppRouter() {
  return createBrowserRouter([
    {
      // /login is a brand-friendly URL that the marketing chrome links
      // to instead of building the GitHub OAuth URL inline (no client_id
      // in static HTML, stable URL for bookmarks). The route immediately
      // redirects to github.com/login/oauth/authorize on mount.
      // /login/callback is GitHub's OAuth redirect target.
      path: ROUTES.LOGIN,
      element: <AuthLayout />,
      children: [
        { index: true, lazy: lazyDefault(() => import('@/features/auth/routes/login.jsx')) },
        {
          path: 'callback',
          lazy: lazyDefault(() => import('@/features/auth/routes/callback.jsx')),
        },
      ],
    },
    {
      path: ROUTES.ADMIN.ROOT,
      element: <ProtectedAppShell />,
      children: [
        {
          path: 'dashboard',
          lazy: lazyDefault(() => import('@/features/dashboard/routes/dashboard.jsx')),
        },
        {
          path: 'profile',
          lazy: lazyDefault(() => import('@/features/profile/routes/profile.jsx')),
        },
        {
          path: 'subscription',
          lazy: lazyDefault(() => import('@/features/subscription/routes/subscription.jsx')),
        },
        {
          path: 'api-keys',
          lazy: lazyDefault(() => import('@/features/api-keys/routes/api-keys.jsx')),
        },
        {
          path: 'repositories',
          lazy: lazyDefault(() => import('@/features/repositories/routes/repositories.jsx')),
        },
        {
          path: 'cloud',
          lazy: lazyDefault(() => import('@/features/cloud/routes/cloud.jsx')),
        },
        {
          path: 'teams',
          lazy: lazyDefault(() => import('@/features/teams/routes/teams.jsx')),
        },
        {
          path: 'teams/:uuid',
          lazy: lazyDefault(() => import('@/features/teams/routes/team-detail.jsx')),
        },
        {
          path: 'analytics',
          lazy: lazyDefault(() => import('@/features/analytics/routes/analytics.jsx')),
        },
        {
          path: 'events',
          lazy: lazyDefault(() => import('@/features/events/routes/events.jsx')),
        },
      ],
    },
    { path: '/', element: <RootRedirect /> },
    { path: '*', element: <Placeholder label="Not Found" /> },
  ]);
}
